import { Paragraph, convertMillimetersToTwip } from 'docx'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry
} from '../../data/model/questionnaire'
import { MarketOption } from '../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { chain } from '../lodashChain'
import {
  getQuestionEntryPrefix,
  getResponseOptions
} from '../questionnaireUtils'
import { createHiddenMarketsLine } from './createHiddenMarketsLine'
import { createResponseLogicText } from './createResponseLogicText'
import { isEntryHiddenInMarket, isItemHiddenInMarket } from './marketFilter'

export const createResponseOptions = (
  entryItem: DraftQuestionItem | DraftMatrixItem,
  entries: DraftQuestionnaireEntry[],
  allMarkets: MarketOption[] = [],
  parentHiddenInMarkets: string[] = [],
  // Per-market export: skip options hidden in this market but keep master
  // codes (computed over the full list), flag inactive master routes to hidden
  // targets and omit the "Hidden in" annotations.
  marketSurveyId?: string
): Paragraph[] => {
  return chain(getResponseOptions(entryItem, entryItem.responseOptions))
    .filter('option.value')
    .sortBy('position')
    .flatMap(({ option, route }, i) => {
      const responseOptionEntry = entryItem.responseOptions.find(
        ({ responseOptionLk }) => responseOptionLk === option.responseOptionId
      )
      if (
        isItemHiddenInMarket(
          responseOptionEntry?.hiddenInMarkets,
          marketSurveyId
        )
      ) {
        return []
      }

      let skipToQuestionText = ''
      // @todo Legacy eslint violation – fix this when editing
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (entries && route?.targetNumber !== undefined) {
        const routeToEntry = entries.find(
          ({ number }) => number === route.targetNumber
        )
        if (routeToEntry) {
          const routeToQuestionPrefixAndNumber =
            getQuestionEntryPrefix(routeToEntry)

          skipToQuestionText = isEntryHiddenInMarket(
            routeToEntry,
            marketSurveyId
          )
            ? ` (master skip to ${routeToQuestionPrefixAndNumber} inactive in this market – ${routeToQuestionPrefixAndNumber} is hidden; respondents continue to the next question)`
            : ` (skip to ${routeToQuestionPrefixAndNumber})`
        }
      }

      const maskingRules = responseOptionEntry?.maskingRules || []

      const optionParagraph = new Paragraph({
        text: `${i + 1}. ${option.value}${skipToQuestionText}`,
        children: createResponseLogicText(maskingRules, entries),
        indent: {
          start: convertMillimetersToTwip(6.3)
        }
      })

      const hiddenLine = createHiddenMarketsLine(
        marketSurveyId ? [] : allMarkets,
        responseOptionEntry?.hiddenInMarkets,
        parentHiddenInMarkets
      )

      return hiddenLine ? [optionParagraph, hiddenLine] : [optionParagraph]
    })
    .value()
}
